import type {
  ComponentPropsWithoutRef,
  ComponentRef,
  ElementType,
  ForwardRefExoticComponent,
  PropsWithoutRef,
  RefAttributes,
} from 'react';
import { createElement, forwardRef } from 'react';

type PolymorphicElementProps<
  TComponent extends ElementType,
  TTag extends ElementType,
  TExtraProps,
> = ComponentPropsWithoutRef<TComponent> & TExtraProps & { as?: TTag };

/**
 * Create a reusable tag selector for next-yak's documented polymorphic pattern.
 * Omitted DOM props are still forwarded to custom components.
 */
export function createPolymorphicElement<
  TComponent extends ElementType,
  TTag extends ElementType = ElementType,
  TRef = ComponentRef<TComponent>,
  TExtraProps = unknown,
>(
  defaultComponent: TComponent & TTag,
  omitDomProps: readonly string[] = []
): ForwardRefExoticComponent<
  PropsWithoutRef<PolymorphicElementProps<TComponent, TTag, TExtraProps>> &
    RefAttributes<TRef>
> {
  return forwardRef<
    TRef,
    PolymorphicElementProps<TComponent, TTag, TExtraProps>
  >(({ as: Component = defaultComponent, ...props }, ref) => {
    const elementProps = { ...props, ref };

    if (typeof Component === 'string') {
      for (const name of omitDomProps) {
        Reflect.deleteProperty(elementProps, name);
      }
    }

    return createElement(Component, elementProps);
  });
}
